import React, { useState } from 'react';
import shortId from 'shortid';
import styled from 'styled-components';
import { FormikValues, useFormikContext } from 'formik';

import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemText from '@material-ui/core/ListItemText';
import FormHelperText from '@material-ui/core/FormHelperText';
import { IChildCategory, IParentCategory } from 'src/interface';
import useGetAllCategories from 'src/hooks/api/categories/useGetAllCategories';
import { useMediaQuery } from '@material-ui/core';
import { BREAKPOINTS } from 'src/utils/constants';

interface IStyledProps {
  isActive?: boolean;
  loading?: boolean;
  isMobile?: boolean;
}
const Section = styled('section')<IStyledProps>`
  display: flex;
  flex-direction: column;
  justify-content: ${({ loading }) => (loading && 'center') || 'flex-start'};
  align-items: center;
  flex-grow: 1;
`;
const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
`;
const ListsContainer = styled.div<IStyledProps>`
  display: flex;
  justify-items: stretch;
  width: 100%;
  max-width: ${({ isMobile }) => (isMobile && '100%') || '50%'};
  .MuiList-root {
    flex: 1 1 100%;
    border: 1px solid #e2e2e2;
    padding-top: 0;
    padding-bottom: 0;
    &:not(:first-child) {
      border-left: none;
    }
    .MuiListItem-root {
      border: 1px solid #e2e2e2;
      border-left: 0;
      border-right: 0;
      border-top: 0;
      &:last-child {
        border-bottom: none;
      }
    }
  }
`;
const Heading = styled.h1``;
const Error = styled(FormHelperText)`
  color: ${({ theme }) => `${theme.colors.error} !important`};
  margin-bottom: 10px !important;
`;

type TProps = {
  stepLoading: boolean;
  handleNext: () => void;
};
const CategoryDetails = ({ stepLoading, handleNext }: TProps) => {
  const isMobile = useMediaQuery(BREAKPOINTS.MOBILE);
  const { Categories: AllCategories, isLoading } = useGetAllCategories();
  const { values, errors, setFieldValue } = useFormikContext<FormikValues>();
  const [selectedParent, setSelectedParent] = useState<IParentCategory>(values.parentCategory);
  const [selectedChild, setSelectedChild] = useState<IChildCategory | null>(
    values.childCategory || null
  );

  const handleParentCategoryItemClick = (
    event: React.MouseEvent<HTMLDivElement, MouseEvent>,
    category: IParentCategory
  ) => {
    setSelectedParent(category);
    setFieldValue('parentCategory', category);
    setSelectedChild(null);
    setFieldValue('childCategory', null);
  };

  const handleChildCategoryItemClick = (
    event: React.MouseEvent<HTMLDivElement, MouseEvent>,
    category: IChildCategory
  ) => {
    setSelectedChild(category);
    setFieldValue('childCategory', category);
    handleNext();
  };

  return (
    <Section loading={stepLoading}>
      <Container>
        <Heading>Select Category</Heading>
        <Error>{errors?.parentCategory || errors?.childCategory}</Error>
        <ListsContainer isMobile={isMobile}>
          <List component="nav" aria-label="secondary mailbox folder">
            {!isLoading &&
              AllCategories?.map((category: IParentCategory) => {
                return (
                  <ListItem
                    key={shortId.generate()}
                    button
                    selected={selectedParent?.id === category.id}
                    onClick={(event) => handleParentCategoryItemClick(event, category)}
                  >
                    <ListItemText primary={category.name} />
                  </ListItem>
                );
              })}
          </List>
          <List component="nav" aria-label="secondary mailbox folder">
            {selectedParent?.children.map((category: IChildCategory) => {
              return (
                <ListItem
                  key={shortId.generate()}
                  button
                  selected={selectedChild?.id === category.id}
                  onClick={(event) => handleChildCategoryItemClick(event, category)}
                >
                  <ListItemText primary={category.name} />
                </ListItem>
              );
            })}
          </List>
        </ListsContainer>
      </Container>
    </Section>
  );
};

CategoryDetails.label = 'Select Category';
CategoryDetails.validate = (values: FormikValues) => {
  const errors: {
    [key: string]: string;
  } = {};
  if (!values.parentCategory) {
    errors.parentCategory = 'Select parent category';
  }
  if (!values.childCategory) {
    errors.childCategory = 'Select child category';
  }
  return errors;
};
CategoryDetails.initialValues = {
  parentCategory: null,
  childCategory: null,
};

export default CategoryDetails;
